/**
 * Knowledge Base API wrappers — thin layer over the shared axios instance.
 * Keeps URL strings and shapes in one place so the React pages stay clean.
 */
import api from './api';

export type KBStatus = 'draft' | 'published' | 'archived';

export interface KBArticleListItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  tags: string[];
  category: string;
  subcategory: string;
  status: KBStatus;
  views: number;
  author: { id: string; firstName: string; lastName: string; username: string; role: string } | null;
  lastEditedBy: { id: string; firstName: string; lastName: string; username: string; role: string } | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KBArticle extends KBArticleListItem {
  content: string;
}

export interface KBListParams {
  q?: string;
  tag?: string;
  category?: string;
  status?: KBStatus;
  limit?: number;
}

export interface KBWriteBody {
  title: string;
  slug?: string;
  excerpt?: string;
  content: string;
  tags?: string[];
  category?: string;
  subcategory?: string;
  status?: KBStatus;
}

function unwrap<T>(res: any): T {
  if (res?.data?.success === false) {
    throw new Error(res.data?.message || 'KB API error');
  }
  return res?.data?.data as T;
}

export const kbApi = {
  async list(params: KBListParams = {}): Promise<{ articles: KBArticleListItem[]; total: number }> {
    const res = await api.get('/api/kb/articles', { params });
    return unwrap(res);
  },

  async get(idOrSlug: string): Promise<{ article: KBArticle }> {
    const res = await api.get(`/api/kb/articles/${encodeURIComponent(idOrSlug)}`);
    return unwrap(res);
  },

  async create(body: KBWriteBody): Promise<{ article: KBArticle }> {
    const res = await api.post('/api/kb/articles', body);
    return unwrap(res);
  },

  async update(id: string, body: Partial<KBWriteBody>): Promise<{ article: KBArticle }> {
    const res = await api.put(`/api/kb/articles/${encodeURIComponent(id)}`, body);
    return unwrap(res);
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/api/kb/articles/${encodeURIComponent(id)}`);
  },

  async categories(): Promise<{ categories: { category: string; count: number }[] }> {
    const res = await api.get('/api/kb/categories');
    return unwrap(res);
  },

  /** Upload media/file (image/video/audio/doc); returns a same-origin URL. */
  async uploadFile(file: File): Promise<{ url: string; fileName: string; size: number; kind: 'image' | 'video' | 'audio' | 'file' }> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post('/api/kb/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(res);
  },

  async tags(): Promise<{ tags: { tag: string; count: number }[] }> {
    const res = await api.get('/api/kb/tags');
    return unwrap(res);
  },

  async taxonomy(): Promise<{
    categories: { category: string; count: number; subcategories: { name: string; count: number }[] }[];
  }> {
    const res = await api.get('/api/kb/taxonomy');
    return unwrap(res);
  },

  /** Rename or delete a category / subcategory across all articles. */
  async editTaxonomy(body: {
    type: 'category' | 'subcategory';
    action: 'rename' | 'delete';
    name: string;
    newName?: string;
    category?: string;
  }): Promise<{ affected: number }> {
    const res = await api.post('/api/kb/taxonomy', body);
    return unwrap(res);
  },
};
